/**
 * Section/tile layout for the AI-Configuration page.
 *
 * Shared by:
 *  - `ai-settings.component` to render tiles for the active section
 *  - `tutor-menu.component` to expose section deep-links as submenu entries
 *
 * Sections map 1:1 to `template_settings.group_key`. Tiles are cosmetic
 * groupings of variable_names within a section — settings that aren't
 * present on a given template are skipped silently.
 */

/**
 * Settings kept in the DB + system prompt but hidden from the UI. Reasons:
 *   - model_name / temperature: we curate these; admins only pick the provider.
 *   - kb_filter_by_course: derived implicitly from the org's Moodle sync
 *     state; no per-tutor toggle. The Tutor × Kurs × KB matrix itself lives
 *     in a dedicated sub-page under "KI-Tutoren" in the main nav.
 * In all cases the `template_settings.default_value` still drives behavior.
 */
export const HIDDEN_VARIABLES = new Set<string>([
  // Guided mode always renders the course canvas: the standalone view is
  // rail | chat | content, and the tutor's lesson text points at what it drew
  // there, so an agent without the panel taught towards an empty half-screen.
  // Definition and code default stay (migration 147 only drops the per-bot
  // values), so the switch can come back without a migration.
  'guided_canvas_enabled',
  'model_name',
  'temperature',
  'kb_filter_by_course',
  // Legacy widget-tab settings. The visible UI now uses the explicit
  // per-tab boolean flags instead.
  'tabs_enabled',
  'enabled_tabs',
  'faq_enabled',
  'enable_question_queue',
  'learnings_store_enabled',
  // Agent loop on the selected model (migration 194): per-agent opt-in while
  // ai_routing_control.agent_loop = opt_in. Its super_admin-only switch sits
  // in the model picker (`app-model-selector`), next to what it switches on.
  'agent_loop_on_selected_model',
  // Agent Learnings (migration 103), MCP connectors (182) and skills (183):
  // the switches live on the agent's own Learnings / MCP pages, next to what
  // they switch on — one home per switch.
  'agent_learnings_enabled',
  'agent_learnings_top_k',
  'agent_mcp_enabled',
  'agent_skills_enabled',
  'agent_skills_max_loaded',
  // Rendered as a dedicated source-toggle tile only when a Twin is linked
  // (see buildTwinTabsToggleTile) — must not leak into a normal tile or
  // the "other" leftovers bucket.
  'use_twin_widget_tabs',
  // Lives on the Allgemein page (assistant-settings-form) as a plain
  // toggle — hidden here so the widget-onboarding switch has exactly one
  // home in the settings UI.
  'onboarding_tour_enabled',
  // Tour scope (explained topics, autostart cap) sits right below that
  // switch on the Allgemein page — same single home.
  'onboarding_tour_topics',
  // Automatic preset of knowledge_mode (migration 224): backend-only state.
  // Picking „Einschränkungen der Wissensbasis“ switches it off server-side.
  'knowledge_mode_auto',
  'onboarding_tour_max_autostarts',
  // Moodle write confirmation (migration 114): reserved for write-as-user
  // actions (forum post, calendar entry) that do not exist yet. No backend
  // reads it, so on every agent type the switch did nothing — and said so in
  // its own tooltip. A switch without an effect is not shown (Lasse,
  // 12.10.2026). Take it out of this list together with the first real write
  // action that honours it.
  'moodle_tools_write_confirm',
  // NOTE: `tab_studyplan_enabled` is now a VISIBLE toggle in the chat_tabs
  // tile (with a visibility_rule so it only appears once the Didaktik feature
  // `study_path_recommendation_enabled` is on). Enabling that feature still
  // auto-activates the studyplan tab in the backend tab-payload builders; the
  // toggle + tooltip just make the tab explicit in the settings.
]);

/**
 * Virtual settings — values backed by columns on the `bots` table rather
 * than by `template_settings`. The tile-based config page surfaces them
 * the same way as normal settings; updates are routed to TutorService
 * .updateTutor() because they don't go through the settings PATCH.
 *
 * We keep this small on purpose. Today only `ai_hint` (the standard chat
 * AI-disclaimer banner toggle) is mirrored here; it's also editable from
 * the Allgemein page and stays in sync because both write the same column.
 *
 * Variable names use the `__bot_` prefix to make their virtual nature
 * obvious in code paths that branch on settings.
 */
export interface VirtualBotSetting {
  variable_name: string;           // unique, prefixed with `__bot_`
  name: string;                    // suffix for `enums.aiConfiguration.<name>`
  field_type: 'boolean';           // booleans are all we need so far
  bot_field: 'ai_hint';            // column on the bots table
  default_value: boolean;
}

export const VIRTUAL_BOT_SETTINGS: VirtualBotSetting[] = [
  {
    variable_name: '__bot_ai_hint',
    name: 'AI-Disclaimer Hinweis',
    field_type: 'boolean',
    bot_field: 'ai_hint',
    default_value: false,
  },
];

export const VIRTUAL_BOT_SETTINGS_BY_VAR: Record<string, VirtualBotSetting> =
  Object.fromEntries(VIRTUAL_BOT_SETTINGS.map((s) => [s.variable_name, s]));

/**
 * Settings rendered directly after the first of their anchors the agent has,
 * instead of at a fixed place in a tile — for a switch that belongs next to a
 * field the templates name differently. „Formell" (`formal_address`,
 * migration 212) follows the agent's style: `style_block` on flexible /
 * support / assist, `tone` („Kommunikationsstil") on alpha_tutor,
 * `teacher_persona` on the Twin.
 */
export const ANCHORED_SETTINGS: Record<string, string[]> = {
  formal_address: ['style_block', 'tone', 'teacher_persona'],
};

/** A tile's variables with each anchored setting inserted after its anchor.
 *  `has` says which variables the agent has; a setting none of whose
 *  anchors exist is not placed. */
export function withAnchoredSettings(
  variables: string[],
  has: (variable: string) => boolean
): string[] {
  const out: string[] = [];
  for (const v of variables) {
    out.push(v);
    for (const [follower, anchors] of Object.entries(ANCHORED_SETTINGS)) {
      if (anchors.find(has) === v) out.push(follower);
    }
  }
  return out;
}

export interface SettingsTile {
  /** Optional — when omitted the tile renders its fields directly without
   *  a sub-heading. Used for the leading tile in a section so it doesn't
   *  introduce a heading rank larger than the section's own h4 above. */
  titleKey?: string;
  hintKey?: string;
  variables: string[];
  /**
   * Optional discriminator for tiles that render a custom component
   * instead of (or in addition to) the standard field list.
   *  - `'faq'` → embeds `<app-faq-list-form [tutor]>` for the per-bot
   *              FAQ table on alpha_support / alpha_assist.
   * Tiles without `kind` render only the standard field list.
   */
  kind?: 'faq';
  /** Key of the section tab (`SettingsSection.tabs`) this tile sits in.
   *  Tiles without one land in the first tab. */
  tab?: string;
}

export interface SettingsSectionTab {
  key: string;
  titleKey: string;
  icon: string;
}

export interface SettingsSection {
  key: string;
  labelKey: string;
  descKey: string;
  tiles: SettingsTile[];
  /** Optional — splits the section's tiles into tabs inside its card
   *  (standalone page only; embedded sections are already a tab). */
  tabs?: SettingsSectionTab[];
}

// Section order reflects the natural configuration flow:
// who is the tutor → what does it know → which model → how does it teach →
// whom is it teaching → what are the limits → what are the safety nets.
export const AI_SETTINGS_SECTIONS: SettingsSection[] = [
  {
    key: 'basis_persona',
    labelKey: 'enums.aiConfigurationSections.basis_persona.label',
    descKey: 'enums.aiConfigurationSections.basis_persona.desc',
    tiles: [
      // Identity tile leads the section. `teacher_introduction` and
      // `support_contact` are learner-facing identity strings; `dozent_name`
      // and `teacher_persona` exist only on alpha_dozent and render here
      // for that template (tutor templates simply skip them — variables
      // not present on the bot are filtered out by getTileSettings).
      // No titleKey — the section's own h4 already heads this group; an
      // extra h5 above the very first tile would invert the heading rank.
      {
        variables: [
          'persona_name',
          'persona_short_description',
          'dozent_name',
          'teacher_persona',
          'teacher_introduction',
          'support_contact',
          // Optional language floor (migration 198), on every template.
          'default_language',
          // Answer only in that language (migration 247), shown while one is set.
          'language_locked',
        ],
      },
      {
        titleKey: 'enums.aiConfigurationTiles.persona',
        // expert_topic stays as its own field (programmatic injection),
        // not buried inside the persona free-text.
        variables: ['persona_block', 'behavior_block', 'expert_topic'],
      },
      {
        titleKey: 'enums.aiConfigurationTiles.style_goal',
        variables: ['style_block', 'goal_block', 'boundaries_block'],
      },
      // Legacy-only tile for templates that still rely on the older free-
      // text prompt fields (tutor_basic, alpha_tutor, …). Hidden entirely
      // for alpha_flexible because none of these variables exist there.
      {
        titleKey: 'enums.aiConfigurationTiles.legacy_prompt',
        variables: [
          'system_prompt_instructions',
          'tone',
          'learning_goals',
        ],
      },
    ],
  },
  {
    key: 'knowledge',
    labelKey: 'enums.aiConfigurationSections.knowledge.label',
    descKey: 'enums.aiConfigurationSections.knowledge.desc',
    tiles: [
      // Retrieval-behavior settings only — the actual KB-bucket pickers
      // live on the dedicated "Wissensbasis" page (top-level menu item),
      // split by purpose tutor / dozent.
      {
        titleKey: 'enums.aiConfigurationTiles.retrieval',
        // `sources_links_enabled` sits after the toggle and carries a
        // visibility_rule so it only renders while include_sources is on.
        variables: ['knowledge_mode', 'include_sources', 'sources_links_enabled', 'search_web'],
      },
    ],
  },
  {
    key: 'model_limits',
    labelKey: 'enums.aiConfigurationSections.model_limits.label',
    descKey: 'enums.aiConfigurationSections.model_limits.desc',
    tabs: [
      {
        key: 'provider',
        titleKey: 'enums.aiConfigurationSections.model_limits.tabs.provider',
        icon: 'options-2-outline',
      },
      {
        key: 'quotas',
        titleKey: 'enums.aiConfigurationSections.model_limits.tabs.quotas',
        icon: 'pie-chart-outline',
      },
    ],
    tiles: [
      {
        tab: 'provider',
        titleKey: 'enums.aiConfigurationTiles.provider',
        hintKey: 'enums.aiConfigurationTiles.provider_hint',
        // Model version and temperature are curated by us → not in the UI.
        variables: ['ai_provider'],
      },
      // Each limit is paired with its enable toggle. The number input
      // sits AFTER the toggle and carries a visibility_rule so it only
      // renders when the toggle is on (driven by `isVisible` in the
      // wizard / AI-settings field renderer).
      {
        tab: 'quotas',
        titleKey: 'enums.aiConfigurationTiles.response_budget',
        hintKey: 'enums.aiConfigurationTiles.response_budget_hint',
        variables: [
          'max_tokens_per_response_enabled',
          'max_tokens_per_response',
        ],
      },
      {
        tab: 'quotas',
        titleKey: 'enums.aiConfigurationTiles.per_user_budget',
        hintKey: 'enums.aiConfigurationTiles.per_user_budget_hint',
        variables: [
          'max_tokens_per_user_day_enabled',
          'max_tokens_per_user_day',
          'max_messages_per_user_day_enabled',
          'max_messages_per_user_day',
          // Monthly token cap — enforced via Redis hot counter +
          // Postgres write-through (user_usage_monthly). Visible
          // only when its toggle is on.
          'max_tokens_per_user_month_enabled',
          'max_tokens_per_user_month',
          // Same ceilings expressed in CREDITS (migration 142) — the billing
          // unit, so operators can cap in the currency they sell. Independent
          // of the token caps: whichever limit is hit first applies.
          'max_credits_per_user_day_enabled',
          'max_credits_per_user_day',
          'max_credits_per_user_month_enabled',
          'max_credits_per_user_month',
        ],
      },
    ],
  },
  {
    key: 'didaktik',
    labelKey: 'enums.aiConfigurationSections.didaktik.label',
    descKey: 'enums.aiConfigurationSections.didaktik.desc',
    tiles: [
      // One tile bundles the didactic toggles the tutor uses every day:
      // learning companion (drives proactive nudges), dialog mode,
      // engagement level, debate mode. Exercises live in their own
      // tile below so the dedicated exercise subgraph and its
      // web-access toggle are visually grouped.
      {
        titleKey: 'enums.aiConfigurationTiles.didactic_modes',
        hintKey: 'enums.aiConfigurationTiles.didactic_modes_hint',
        variables: [
          'live_actions',
          'live_actions_frequency',
          'dialog_mode',
          'engagement_level',
          'debate_mode_enabled',
          // Orbi-Begleiter im Chat-Widget (migration 184): schaut hinter dem
          // Chatfenster hervor und feiert richtige Antworten.
          'orbi_companion_enabled',
          // Kursname im Chat-Kopf (migration 237, alle alpha_*): unter dem
          // Agent-Namen steht, auf welchen Kurs sich der Chat bezieht.
          'show_course_name',
          // Interaktive Lernhilfen unter der Antwort (migration 214, nur
          // alpha_tutor / alpha_flexible): Karteikarten, Kacheln, Rechner, …
          'learning_blocks_enabled',
          // Wie oft (migration 240): vereinzelt / ausgewogen / häufig — nur
          // sichtbar bei eingeschalteten Lernhilfen (visibility_rule).
          'learning_blocks_frequency',
          // Anschlussfragen unter der Antwort (migration 223, nur alpha_tutor /
          // alpha_flexible): bis zu drei klickbare, im Kurs verankerte Fragen.
          'follow_up_suggestions_enabled',
          // Study Plan (migration 117) — master toggle. The paired tab flag
          // `tab_studyplan_enabled` lives in the chat_tabs tile (functions
          // section) with a visibility_rule on this setting.
          'study_path_recommendation_enabled',
          // Which plan the agent creates and uses until the learner chooses
          // another (migration 233): one per course or a personal one.
          // visibility_rule on the master toggle above.
          'study_plan_default_kind',
          // Guided Learning Journey / standalone teaching mode (migration
          // 119). Master toggle; the learner then starts it from the chat
          // action menu ("Geführten Modus starten") or by asking to be
          // guided through the course. Only meaningful once the course has
          // a parsed structure.
          'guided_mode_enabled',
          // Guided-mode init behaviour (migration 122) — carries a
          // visibility_rule on `guided_mode_enabled`, so it only renders
          // while the master toggle is on. Autostart drops every connecting
          // learner straight into the guided journey (which always opens as
          // the fullscreen standalone view — the separate fullscreen toggle
          // was dropped in migration 146).
          'guided_mode_autostart',
          // Admin step limit (migration 139): caps guided steps per learner
          // per calendar week/month. Count + period only render while the
          // limit toggle is on (visibility_rule chain).
          'guided_step_limit_enabled',
          'guided_step_limit',
          'guided_step_limit_period',
        ],
      },
      // Exercise subgraph + its dependent options. exercise_web_access,
      // exercise_curated_only and exercise_types carry a visibility_rule so
      // they only render when exercise_mode is on (see migrations 020/105).
      {
        titleKey: 'enums.aiConfigurationTiles.exercises',
        hintKey: 'enums.aiConfigurationTiles.exercises_hint',
        variables: [
          'exercise_mode',
          'exercise_curated_only',
          'exercise_web_access',
          'exercise_types',
        ],
      },
    ],
  },
  // Dozent section serves two different audiences:
  //   - on a tutor-bot (alpha_tutor / alpha_flexible): hosts the dozent-
  //     link picker as a virtual tile rendered by the AI-config page.
  //     No template_settings live here for tutors anymore — section
  //     becomes visible only when the picker tile renders.
  //   - on an alpha_dozent bot: hides entirely (its own settings live
  //     in the new `functions` section below).
  {
    key: 'dozent',
    labelKey: 'enums.aiConfigurationSections.dozent.label',
    descKey: 'enums.aiConfigurationSections.dozent.desc',
    tiles: [],
  },
  // Functions section — alpha_dozent only. Exposes the learner-visible
  // widget tabs plus per-function configuration tiles (materials limits).
  {
    key: 'functions',
    labelKey: 'enums.aiConfigurationSections.functions.label',
    descKey: 'enums.aiConfigurationSections.functions.desc',
    tiles: [
      {
        titleKey: 'enums.aiConfigurationTiles.chat_tabs',
        hintKey: 'enums.aiConfigurationTiles.chat_tabs_hint',
        variables: [
          'tab_exercises_enabled',
          'tab_messages_enabled',
          'tab_notifications_enabled',
          'tab_materials_enabled',
          // Study Plan tab (migration 117) — only meaningful when the Didaktik
          // master `study_path_recommendation_enabled` is on (visibility_rule).
          'tab_studyplan_enabled',
          // Learner Profile tab (migration 118). What it DISPLAYS
          // (learner_profile_display_mode) is configured in the Lernerprofil
          // section, not here — this is just the tab on/off toggle.
          'tab_learnerprofile_enabled',
        ],
      },
      // Lernmaterialien configuration — only meaningful while
      // `tab_materials_enabled` is on (visibility_rule on each setting,
      // migration 091). Limits are the paired toggle+int pattern (024).
      {
        titleKey: 'enums.aiConfigurationTiles.materials',
        hintKey: 'enums.aiConfigurationTiles.materials_hint',
        variables: [
          // Which material types learners may generate (migration 210,
          // multi-select summary/flashcards/quiz/mindmap). Empty = no new
          // material can be generated; saved materials are unaffected.
          'materials_allowed_types',
          // Personalized summaries (migration 213): clarifying questions
          // before writing, one-click suggestions in the tab.
          'materials_clarify_enabled',
          'materials_suggestions_enabled',
          'materials_download_enabled',
          'materials_max_saved_total_enabled',
          'materials_max_saved_total',
          'materials_max_generations_per_month_enabled',
          'materials_max_generations_per_month',
        ],
      },
      // Moodle lecturer functions (migration 150) — this Twin's teachers
      // answer learners and send exercises from inside Moodle. Master
      // toggle plus who of them; the person picker under it is rendered
      // as a special case in the template, because the list lives in
      // `bot_moodle_dozent_access` and not in `template_settings`.
      {
        titleKey: 'enums.aiConfigurationTiles.moodle_dozent_tools',
        hintKey: 'enums.aiConfigurationTiles.moodle_dozent_tools_hint',
        variables: [
          'moodle_dozent_tools_enabled',
          'moodle_dozent_audience',
        ],
      },
    ],
  },
  // Moodle agent tools (migration 113) — let the agent read the learner's
  // Moodle context live (progress, grades, personal calendar, forum
  // activity, course structure). Master toggle + capability-group scope;
  // default off. Scope/confirm are gated on the master toggle via
  // visibility_rule. Own section since 29.09.2026: the tab „Moodle-Aktionen"
  // of Agent › Verhalten & Fähigkeiten (was a tile of `functions`). The
  // settings belong to THIS agent even when a Twin drives the widget tabs —
  // as its own section it is no longer caught by the Twin branch.
  {
    key: 'moodle_actions',
    labelKey: 'enums.aiConfigurationTiles.moodle_tools',
    descKey: 'enums.aiConfigurationTiles.moodle_tools_hint',
    tiles: [
      {
        variables: [
          'moodle_tools_enabled',
          'moodle_tools_scope',
          'moodle_tools_write_confirm',
        ],
      },
    ],
  },
  {
    key: 'learner_profile',
    labelKey: 'enums.aiConfigurationSections.learner_profile.label',
    descKey: 'enums.aiConfigurationSections.learner_profile.desc',
    tiles: [
      {
        titleKey: 'enums.aiConfigurationTiles.profile_tracking',
        variables: [
          'learner_profile_enabled',
          'track_learning_progress',
          'learner_level',
          'learning_level',
          // What the Lernerprofil widget tab shows (migration 118 / 121):
          // moodle_only = imported Moodle data · full = + dynamic profile.
          // Lives here (Lernerprofil section) so the display scope is defined
          // next to the profile settings, not with the tab on/off toggle.
          'learner_profile_display_mode',
        ],
      },
    ],
  },
  {
    // Both curriculum switches in one place. They were a screen apart — one
    // under "Lernerprofil", one under "Wissen" — and nothing said how they
    // differ. Side by side the distinction is readable: one is about this
    // learner, the other about this course.
    key: 'curriculum',
    labelKey: 'enums.aiConfigurationSections.curriculum.label',
    descKey: 'enums.aiConfigurationSections.curriculum.desc',
    tiles: [
      // One tile, two switches. A tile per setting was the only place in this
      // page that did that, and it showed: the tile heading repeated the
      // setting label right below it, and each heading carried its own
      // paragraph of prose. Everywhere else a tile groups settings and the
      // rows carry their own names — so does this one now, headerless because
      // the section heading already says "Curriculum".
      //
      // Structure first: it stands on its own, the learner status builds on
      // it. Migration 168 puts the same order in `display_order` so the
      // wizard, which reads the database, no longer shows them reversed.
      {
        variables: ['curriculum_context_enabled', 'curriculum_awareness'],
      },
    ],
  },
  {
    key: 'guardrails',
    labelKey: 'enums.aiConfigurationSections.guardrails.label',
    descKey: 'enums.aiConfigurationSections.guardrails.desc',
    tiles: [
      {
        titleKey: 'enums.aiConfigurationTiles.homework_guard',
        variables: ['prevent_homework_solving', 'homework_policy_text'],
      },
      {
        // Chat disclaimer. The key stays `disclaimer` (its value now reads
        // "Chat-Disclaimer") — renamed via i18n value only (CLAUDE.md rule 11).
        titleKey: 'enums.aiConfigurationTiles.disclaimer',
        // `__bot_ai_hint` is the standard AI-disclaimer banner (Bot column),
        // available across all templates the same way it is in tutor_basic.
        // Followed by the alpha_flexible custom disclaimer (toggle + text).
        variables: ['__bot_ai_hint', 'disclaimer_enabled', 'disclaimer_text'],
      },
      // Material disclaimer (migration 210). Generated learning materials are
      // ALWAYS labelled as AI-generated (badge + standard notice in the
      // learner's UI language) — not switchable. Only alpha_flexible carries
      // these two settings, to replace the standard notice with its own text;
      // on every other template the tile has no settings and is dropped by
      // the empty-tile filter (ai-settings.component / tutor-wizard).
      {
        titleKey: 'enums.aiConfigurationTiles.disclaimer_materials',
        hintKey: 'enums.aiConfigurationTiles.disclaimer_materials_hint',
        variables: [
          'materials_disclaimer_custom_enabled',
          // visibility_rule: only while the custom toggle is on.
          'materials_disclaimer_text',
        ],
      },
    ],
  },
  // FAQ section — surfaced on alpha_support / alpha_assist when their
  // `bot_faq_enabled` template_setting is present. Distinct variable
  // name from the dozent template's `faq_enabled` (which drives the
  // teacher-FAQ curation pipeline) so the two FAQ surfaces stay
  // independent. The tile renders the dedicated FAQ-list editor
  // (Topic / Tags / Question / Answer) backed by the `bot_faqs` table.
  {
    key: 'faq',
    labelKey: 'enums.aiConfigurationSections.faq.label',
    descKey: 'enums.aiConfigurationSections.faq.desc',
    tiles: [
      {
        kind: 'faq',
        variables: ['bot_faq_enabled'],
      },
    ],
  },
  // Integration section — alpha_support only. Ticket hand-off (email /
  // Slack / Teams / webhook) plus the optional reply-back toggle. The
  // backend handler isn't built yet; the UI renders the fields as
  // disabled with a "Coming Soon" hint.
  {
    key: 'integration',
    labelKey: 'enums.aiConfigurationSections.integration.label',
    descKey: 'enums.aiConfigurationSections.integration.desc',
    tiles: [
      // E-Mail-tool tile — fully implemented (SES via boto3). When the
      // toggle is on the alpha_support agent gets the
      // `send_support_email` tool bound and is instructed via prompt
      // to use it after explicit user opt-in.
      {
        titleKey: 'enums.aiConfigurationTiles.email_tool',
        hintKey: 'enums.aiConfigurationTiles.email_tool_hint',
        variables: [
          'email_tool_enabled',
          'email_tool_from_local',
          'email_tool_to',
        ],
      },
      // Ticket-handoff tile (email/Slack/Teams/webhook). All vars are
      // tagged `Coming Soon` by the tutor-settings renderer (see
      // `COMING_SOON_PREFIXES`) — backend dispatcher is not built yet.
      {
        titleKey: 'enums.aiConfigurationTiles.integration_handoff',
        hintKey: 'enums.aiConfigurationTiles.integration_handoff_hint',
        variables: [
          'integration_enabled',
          'integration_channel',
          'integration_email_to',
          'integration_slack_webhook',
          'integration_teams_webhook',
          'integration_custom_webhook',
          'integration_human_handoff_reply_enabled',
        ],
      },
    ],
  },
];

/**
 * Sections that no longer sit under KI-Konfiguration but are tabs of other
 * agent pages (embedded via AI_SETTINGS_EMBEDDED_SECTION): section → route
 * below /tutors/:id. The agent menu leaves them out of the KI-Konfiguration
 * sub-entries, KI-Konfiguration never falls back to them, and
 * /ai-settings/<section> redirects to the tab (app-routing.module.ts).
 */
export const SECTIONS_ELSEWHERE: Record<string, string> = {
  knowledge: 'knowledge/usage',
  didaktik: 'learning/didaktik',
  dozent: 'learning/dozent',
  functions: 'learning/functions',
  learner_profile: 'learning/learner_profile',
  curriculum: 'learning/curriculum',
  moodle_actions: 'setup/moodle',
};

/**
 * Sections whose visibility doesn't depend on template_settings — these
 * render virtual content (e.g. the dozent-link picker on tutor templates).
 * The caller must pass the right context flag for them to appear.
 */
export const VIRTUAL_SECTIONS: Record<string, (ctx: SectionContext) => boolean> = {
  // Dozent section becomes visible on tutor-bots that can attach a
  // dozent-bot via bot_dozent_link — the AI-config page renders the
  // picker as a special-cased tile inside this section.
  dozent: (ctx) => !!ctx.isLinkableTutor,
  // Linkable tutor templates edit the attached dozent-bot's function
  // toggles from their own settings page. Those rows are not part of
  // the tutor template_settings response, so keep the section available
  // as virtual content.
  functions: (ctx) => !!ctx.isLinkableTutor,
};

export interface SectionContext {
  /** True for alpha_tutor / alpha_flexible bots — surfaces the dozent-
   *  link picker. */
  isLinkableTutor?: boolean;
}

/**
 * Given a flat list of settings (with `variable_name`), return the subset of
 * sections that actually contain at least one *visible* setting from the
 * list, in the order declared above. Hidden variables (see HIDDEN_VARIABLES)
 * never count towards a section being available. Virtual bot-backed
 * settings are always considered "present" because they exist for every
 * template via the `bots` table. Virtual sections (see VIRTUAL_SECTIONS)
 * are kept when their context predicate matches.
 */
export function filterSectionsByAvailableSettings<T extends { variable_name?: string }>(
  settings: T[],
  ctx: SectionContext = {}
): SettingsSection[] {
  const present = new Set<string>(Object.keys(VIRTUAL_BOT_SETTINGS_BY_VAR));
  for (const s of settings) {
    if (s.variable_name && !HIDDEN_VARIABLES.has(s.variable_name)) {
      present.add(s.variable_name);
    }
  }
  return AI_SETTINGS_SECTIONS.filter((sec) => {
    const virtualCheck = VIRTUAL_SECTIONS[sec.key];
    if (virtualCheck && virtualCheck(ctx)) return true;
    return sec.tiles.some((tile) =>
      tile.variables.some((v) => present.has(v))
    );
  });
}
